import * as React from 'react';
import { View, ScrollView } from 'react-native';
import { Text, Icon } from 'react-native-elements';
import moment from 'moment';
import { useFocusEffect } from '@react-navigation/native';
import BillingScreenProps from 'types/screens/BillingScreenProps';
import Product from 'types/models/Product';
import UserRoleType from 'types/enums/UserRoleType';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import OrderContext from 'contexts/OrderContext';
import AppContext from 'contexts/AppContext';
import { styles } from 'styles/screens/billingScreen';
import { getTotalBillingAndCommercesAmount } from 'services/ordersService';
import { getProductsSoldByCommerce } from 'services/productService';
import InputSearch from 'components/InputSearch';
import ProductsSoldList from 'components/ProductsSoldList';
import { useTranslation } from 'react-i18next';
import useResponsiveContainer from 'hooks/useResponsiveContainer';
import parseAmounts from 'utils/parseAmounts';
import { SupportedLanguages } from 'types/enums/LanguageType';

export default ({ navigation }: BillingScreenProps): JSX.Element => {
  const mediumScreen = useMediumScreen();
  const ResponsiveContainer = useResponsiveContainer();
  const { deleteAuthData } = useChangeAuthData();
  const { currentCommerce, currentVenue } = React.useContext(OrderContext);
  const { setModalData, authData } = React.useContext(AppContext);
  const [totalBillingCommerce, setTotalBillingCommerce] = React.useState<number | null>(null);
  const [productsSoldByCommerce, setProductsSoldByCommerce] = React.useState<Product[] | null>(null);
  const [filteredProductsSoldByCommerce, setFilteredProductsSoldByCommerce] = React.useState<Product[] | null>(null);
  const [inputText, setInputText] = React.useState('');
  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm') : null;
  const unitsSoldAmongAllCommerceProducts = productsSoldByCommerce
    ? productsSoldByCommerce?.reduce((acc, product) => {
        if (product.amountSold) {
          return acc + product.amountSold;
        } else {
          return acc;
        }
      }, 0)
    : null;
  const leftStockOfSoldProductsOnCommerce = productsSoldByCommerce
    ? productsSoldByCommerce?.reduce((acc, product) => {
        return acc + product.stock;
      }, 0)
    : null;

  const { t, i18n } = useTranslation();

  const fetchBillingTotalBillingByCommerce = async () => {
    if (!currentCommerce || !currentVenue) return;
    if (isCommerceEditor) return;
    try {
      const total = await getTotalBillingAndCommercesAmount({
        commerceIds: [currentCommerce.id],
        billingRange: currentVenue.billingRange,
      });
      if (total !== null) setTotalBillingCommerce(total.balance);
    } catch (error) {
      console.log(error);
      setTotalBillingCommerce(null);
    }
  };

  const fetchProductsSoldByCommerce = async () => {
    if (!currentCommerce || !currentVenue) return;
    try {
      const productsSold = await getProductsSoldByCommerce(
        currentCommerce.id,
        currentVenue?.billingRange,
        currentVenue?.localDateTimeIdentifier,
      );

      if (productsSold) {
        setProductsSoldByCommerce(productsSold);
      }
    } catch (error) {
      console.log(error);
      setProductsSoldByCommerce(null);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      if (currentVenue && currentCommerce) {
        fetchBillingTotalBillingByCommerce();
        fetchProductsSoldByCommerce();
      } else {
        setTimeout(() => {
          navigation.reset({
            index: 1,
            routes: [{ name: 'VenueSelectionScreen' }, { name: 'CommercesScreen' }],
          });
        }, 500);
      }
    }, [currentCommerce]),
  );

  return (
    <ResponsiveContainer style={styles.container}>
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          tvParallaxProperties={null}
          name={'arrow-back-outline'}
          type="ionicon"
          color="#001C47"
          size={40}
          containerStyle={mediumScreen ? styles.goBackContainerLarge : styles.goBackContainerSmall}
          onPress={() => {
            navigation.goBack();
          }}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLargeBilling}>
              <Text
                style={[styles.textTitleLargeBilling, styles.textTitleLargeCommerce]}
              >{`${currentCommerce?.name} >`}</Text>
              {` Stock`}
            </Text>
            <View style={{ marginTop: 25, marginLeft: 45 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: '400',
                  lineHeight: 20,
                  letterSpacing: 0.25,
                  textAlign: 'left',
                  color: '#001639',
                }}
              >{`${currentDateTime}hs`}</Text>
            </View>
          </View>
        ) : (
          <Text style={mediumScreen ? styles.textLarge : styles.textSmall}>{currentCommerce?.name}</Text>
        )}
        {mediumScreen ? (
          <InputSearch
            value={inputText}
            setInput={setInputText}
            handleSearch={async () => {
              if (!productsSoldByCommerce) return;
              if (!inputText || inputText == '-1') {
                setFilteredProductsSoldByCommerce(null);
                setProductsSoldByCommerce(productsSoldByCommerce);
              }

              const patternRegex = new RegExp(`(${inputText})`, 'gi');
              const filteredProducts = productsSoldByCommerce.filter((product) => patternRegex.test(product.name));

              if (filteredProducts && filteredProducts.length > 0) {
                setFilteredProductsSoldByCommerce(filteredProducts);
              }
            }}
            isMediumScreen={mediumScreen}
            placeholderText={t('search-by-product')}
            keyboardType="default"
          />
        ) : (
          <Icon
            tvParallaxProperties={null}
            name="person-circle-sharp"
            type="ionicon"
            color="#001639"
            size={40}
            containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
            onPress={() =>
              setModalData({
                description: t('logout'),
                generalColor: currentVenue?.appColor ?? '#001C47',
                action: async () => {
                  await deleteAuthData();
                },
              })
            }
          />
        )}
      </View>
      {!mediumScreen && (
        <Text
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignSelf: 'flex-start',
            marginLeft: 40,
            marginBottom: 10,
            fontSize: 16,
            fontWeight: '500',
            lineHeight: 24,
            letterSpacing: 0,
            textAlign: 'left',
          }}
        >
          Stock
        </Text>
      )}
      <View
        style={{
          flexWrap: mediumScreen ? 'wrap' : 'nowrap',
          flexDirection: mediumScreen ? 'row' : 'column',
          marginHorizontal: mediumScreen ? 35 : 0,
        }}
      >
        <View
          style={[
            mediumScreen ? styles.totalContainerLarge : styles.totalContainerSmall,
            { backgroundColor: currentVenue?.appColor ?? '#fff' },
          ]}
        >
          <View
            style={[
              mediumScreen ? styles.totalTextContainerAdjustmentsLarge : styles.totalTextContainerAdjustmentsSmall,
            ]}
          >
            <Text style={mediumScreen ? styles.totalTextTitleLarge : styles.totalTextTitleSmall}>
              {t('total-sales')}
            </Text>
            <Text style={mediumScreen ? styles.totalTextBalanceLarge : styles.totalTextBalanceSmail}>
              {parseAmounts(totalBillingCommerce ?? 0, i18n.language as SupportedLanguages)}
            </Text>
          </View>
        </View>
        <View style={mediumScreen ? styles.cardsContainerLarge : styles.cardsContainerSmall}>
          <View style={styles.cardColumn}>
            <View style={[mediumScreen ? styles.billingResumeContainerLarge : styles.billingResumeContainerSmall]}>
              <Text style={[mediumScreen ? styles.billingResumeTitleLarge : styles.billingResumeTitleSmall]}>
                {t('total-sold')}
              </Text>
              <Text style={mediumScreen ? styles.billingResumeNumberLarge : styles.billingResumeNumberSmall}>
                {unitsSoldAmongAllCommerceProducts}
              </Text>
            </View>
          </View>
          <View style={styles.cardColumn}>
            <View style={[mediumScreen ? styles.billingResumeContainerLarge : styles.billingResumeContainerSmall]}>
              <Text style={[mediumScreen ? styles.billingResumeTitleLarge : styles.billingResumeTitleSmall]}>
                {t('stock-total')}
              </Text>
              <Text style={mediumScreen ? styles.billingResumeNumberLarge : styles.billingResumeNumberSmall}>
                {leftStockOfSoldProductsOnCommerce}
              </Text>
            </View>
          </View>
        </View>
      </View>
      <ScrollView
        style={mediumScreen ? styles.productsContainerLarge : styles.productsContainerSmall}
        contentContainerStyle={styles.scrollProductsLarge}
      >
        <View style={mediumScreen ? styles.productsInsideContainerLarge : styles.productsInsideContainerSmall}>
          {productsSoldByCommerce && (
            <ProductsSoldList
              products={
                filteredProductsSoldByCommerce && filteredProductsSoldByCommerce.length > 0
                  ? filteredProductsSoldByCommerce
                  : productsSoldByCommerce
              }
              isMediumScreen={mediumScreen}
            >
              <>
                {!mediumScreen && (
                  <View style={mediumScreen ? styles.header : styles.headerSmall}>
                    <InputSearch
                      value={inputText}
                      setInput={setInputText}
                      handleSearch={async () => {
                        if (!inputText || inputText == '-1') {
                          setFilteredProductsSoldByCommerce(null);
                          setProductsSoldByCommerce(productsSoldByCommerce);
                        }

                        const patternRegex = new RegExp(`(${inputText})`, 'gi');
                        const filteredProducts = productsSoldByCommerce.filter((product) =>
                          patternRegex.test(product.name),
                        );

                        if (filteredProducts && filteredProducts.length > 0) {
                          setFilteredProductsSoldByCommerce(filteredProducts);
                        }
                      }}
                      isMediumScreen={mediumScreen}
                      placeholderText={t('search-by-product')}
                      keyboardType="default"
                    />
                  </View>
                )}
                <View
                  style={[
                    styles.topRowForDescriptionOnListOrders,
                    { backgroundColor: currentVenue?.appColor ?? '#000000' },
                  ]}
                >
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'center' }}>
                    <Text style={styles.topRowTextForDescription}>{t('products')} ↑</Text>
                  </View>
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'center' }}>
                    <Text style={styles.topRowTextForDescription}>Stock</Text>
                  </View>
                  <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <Text style={styles.topRowTextForDescription}>{t('sold')}</Text>
                  </View>
                </View>
              </>
            </ProductsSoldList>
          )}
        </View>
      </ScrollView>
    </ResponsiveContainer>
  );
};
